import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { OrganizationStateService } from 'src/app/shared/organization-state.service';
import { AppProfile } from '../../../shared/app-profile/app-profile';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class OrganizationSettingsService {
  constructor(
    private readonly http: HttpClient,
    private readonly authSvc: AuthService,
    private readonly orgState: OrganizationStateService,
    private readonly appProfile: AppProfile
  ) {}

  getOrgSetting(
    orgId: string,
    settingId: string,
    token: string
  ): Observable<any> {
    const headers = this.authSvc.getCommonHeaders(token, '1.0.0');
    return this.http.get<any>(
      `${this.appProfile.epUs}/organization-ms/orgs/${orgId}/settings/${settingId}`,
      { headers }
    );
  }

  updateOrgSetting(
    orgId: string,
    settingId: string,
    value: number | string | { value: number | string; parentSettingId?: any },
    token: string
  ): Observable<any> {
    const headers = this.authSvc.getCommonHeaders(token, '1.0.0');
    const url = `${this.appProfile.epUs}/organization-ms/orgs/${orgId}/settings/${settingId}`;

    const isObjectWithValue = (
      val: any
    ): val is { value: number | string; parentSettingId?: any } =>
      typeof val === 'object' && val !== null && 'value' in val;

    const payload = isObjectWithValue(value)
      ? {
          orgId: orgId,
          orgSetting: {
            groupSettingId: settingId,
            enabled: true,
            hidden: false,
            overridable: true,
            sortOrder: '0',
            categories: ["EntitySetting"],
            value: value.value.toString(),
            parentSettingId: value.parentSettingId || null,
            childSettings: [],
          },
        }
      : {
          orgId: orgId,
          orgSetting: {
            groupSettingId: settingId,
            enabled: true,
            hidden: false,
            overridable: true,
            sortOrder: '0',
            categories: ["EntitySetting"],
            value: value.toString(),
            parentSettingId: null,
            childSettings: [],
          },
        };

    return this.http.put<any>(url, payload, { headers });
  }

  /**
   * Returns an observable with the correct orgId (and a flag if settings should be shown), using permission-aware logic.
   */
  getPermissionAwareOrgId() {
    const permissions = this.authSvc.userPermissions || [];
    if (permissions.includes('AccessProductSupportConsole')) {
      return this.orgState.orgResponse$.pipe(
        map((org) => ({
          orgId: org?.orgId || org?.id || null,
          showSettings: !!(org?.orgId || org?.id),
        }))
      );
    } else {
      const orgId = this.authSvc.userTopOrgId || null;
      return of({ orgId, showSettings: !!orgId });
    }
  }
}
